;(function () {
  /** @type {google.maps.Marker[]} */
  const markers = []

  /** Map<number, google.maps.Marker> — id ➜ marker (≈ markers-index*) */
  const markersIndex = new Map()

  /** Map<number, {available?: boolean, …}> — id ➜ lightweight offer hash */
  const offersIndex = new Map()

  /**
   * Return an array of markers currently visible on the map.
   * @returns {google.maps.Marker[]}
   */
  function getVisibleMarkers() {
    // `markers` is the global array of all markers on the map
    return markers.filter((marker) => marker.isVisible())
  }

  /**
   * Return a Set of IDs of markers currently visible on the map.
   * @returns {Set<number>}
   */
  function getVisibleMarkerIds() {
    const visibleMarkers = getVisibleMarkers()
    return new Set(visibleMarkers.map((marker) => markerId(marker)))
  }

  function resetMarkers(newValue) {
    markers.length = 0
    markersIndex.clear()
    if (newValue) {
      markers.push(...newValue)
      for (const m of newValue) {
        markersIndex.set(markerId(m), m)
      }
    }
  }

  function removeAllMarkers() {
    for (const marker of markers) {
      marker.remove()
    }
    resetMarkers()
  }

  function markerId(marker) {
    return marker.id
  }

  function getMarkerById(id) {
    return markersIndex.get(id)
  }

  /**
   * Convert a google.maps.LatLngBounds → plain BoundingBox
   * { sw_lat, sw_lng, ne_lat, ne_lng }
   */
  function boundsToBoundingBox(bounds) {
    const sw = bounds.getSouthWest()
    const ne = bounds.getNorthEast()
    const result = {
      sw_lat: sw.lat(),
      sw_lng: sw.lng(),
      ne_lat: ne.lat(),
      ne_lng: ne.lng(),
    }

    return Object.values(result).join("_")
  }

  /**
   * Build the params object expected by /map/markers
   *
   * @param {google.maps.Map} gmap                    – current map instance
   * @param {Object}  prev                            – { bounds: LatLngBounds|null, zoom: number|null }
   * @param {number}  clusteringThreshold
   * @param {number}  kingpinId
   * @param {string}  recommendedPositionStatus       – whatever is in `PropertyRecommendedPosition.user_status`
   *
   * @returns {{params: Object, nextPrev: Object}}
   */
  function buildFetchMarkersParams(
    gmap,
    prev,
    { clusteringThreshold, kingpinId, recommendedPositionStatus },
  ) {
    const zoom = gmap.getZoom?.() // `undefined` until map is ready
    const bounds = gmap.getBounds?.() // ditto

    if (zoom === undefined || !bounds) {
      return { params: null, nextPrev: prev } // nothing to fetch yet
    }

    // ----- union only on the same zoom level -----
    let effectiveBounds
    if (prev.bounds && prev.zoom === zoom) {
      effectiveBounds = prev.bounds.union(bounds) // google.maps.LatLngBounds.union
    } else {
      effectiveBounds = bounds
    }

    const params = {
      bounding_box: boundsToBoundingBox(effectiveBounds),
      zoom,
      clustering_threshold: clusteringThreshold,
      kingpin_id: kingpinId,
      recommended_position_status: recommendedPositionStatus,
    }

    return {
      params,
      nextPrev: { bounds: effectiveBounds, zoom },
    }
  }

  /**
   * Handle a fresh batch of marker data coming from /map/markers.
   *
   * @param {Object}  opts
   * @param {boolean} opts.incremental           – whether the fetch was incremental
   * @param {Function} opts.dataToMarkerFn       – (record) → google.maps.Marker
   * @param {number?} opts.preloadOfferId        – king-pin offer to preload (ignored for now)
   *
   * @returns {(data: any[]) => void}            – callback passed straight to fetch().then()
   */
  function processFetchedMarkers({
    incremental,
    dataToMarkerFn,
    preloadOfferId,
    offersUrl,
  }) {
    return function process(data) {
      // 1 — prune markers that disappeared from the response
      removeStaleMarkersFromMap(data)

      // 2 — create / attach any new ones
      addNewMarkersToMap(data, dataToMarkerFn)

      // 3 — fetch offers
      if (preloadOfferId) {
        fetchOffers(incremental, [preloadOfferId], offersUrl)
        fetchOffers(incremental, null, offersUrl)
      } else {
        fetchOffers(incremental, null, offersUrl)
      }
    }
  }

  /**
   * Create a closure that converts raw marker JSON into `new MapMarker(...)`.
   *
   * @param {Object}   opts
   * @param {google.maps.Map} opts.gmap
   * @param {number}         [opts.kingpinId]       – highlighted property id
   * @param {Function}       [opts.onClickMarker]   – marker click handler
   *
   * @param {string}  markerAriaLabel
   * @returns {(rec: {id:number, latitude:number, longitude:number}) ⇒ google.maps.Marker}
   */
  function buildDataToMarker({
    gmap,
    kingpinId,
    onClickMarker,
    onMouseEnterMarker,
    onMouseLeaveMarker,
    loadingHtml,
    markerAriaLabel,
  }) {
    return function dataToMarker({ latitude, longitude, id, position }) {
      const offer = offersIndex.get(id)
      const classes = []

      if (id === kingpinId)
        classes.push("google-maps-marker--container_kingpin")
      if (offer && offer.available === false)
        classes.push("google-maps-marker--container_unavailable")

      return new MapMarker({
        position: { lat: latitude, lng: longitude },
        ariaLabel: markerAriaLabel,
        content: loadingHtml,
        onClick: onClickMarker,
        onMouseEnter: onMouseEnterMarker,
        onMouseLeave: onMouseLeaveMarker,
        containerClasses: classes.length ? classes : undefined,
        map: gmap,
        propertyRecommendedPosition: position,
        id,
      })
    }
  }

  /**
   * Adds only the markers that aren’t already on the map.
   *
   * @param {any[]}   data              – raw records returned from /map/markers
   * @param {Function} dataToMarkerFn   – (record) → google.maps.Marker
   */
  function addNewMarkersToMap(data, dataToMarkerFn) {
    if (!data || data.length === 0) return

    // skip ids that are already present (uses markersIndex instead of standalone Set)
    const onlyNewData = data.filter((item) => !markersIndex.has(item.id))
    if (onlyNewData.length === 0) return

    const onlyNewMarkers = onlyNewData.map(dataToMarkerFn)

    for (const marker of onlyNewMarkers) {
      const id = markerId(marker)
      markers.push(marker)
      markersIndex.set(id, marker)
      // no separate Set to update – getPlacedMarkerIds() derives from markersIndex
    }
  }

  /**
   * Remove markers that are no longer present in the latest payload.
   *
   * @param {any[]} data – raw records from /map/markers
   */
  function removeStaleMarkersFromMap(data) {
    if (!data || data.length === 0) return

    const idsSet = new Set(data.map((d) => d.id))

    /** @type {google.maps.Marker[]} */
    const actualMarkers = []
    /** @type {google.maps.Marker[]} */
    const staleMarkers = []

    // partition current markers into “still present” vs “stale”
    for (const m of markers) {
      ;(idsSet.has(markerId(m)) ? actualMarkers : staleMarkers).push(m)
    }

    // 1 — remove stale ones from the map and index
    for (const m of staleMarkers) {
      if (typeof m.remove === "function") {
        m.remove() // custom wrapper
      } else if (typeof m.setMap === "function") {
        m.setMap(null) // vanilla GMaps fallback
      }
      markersIndex.delete(markerId(m))
    }

    // 2 — replace the shared markers[] array with the survivors
    markers.length = 0
    markers.push(...actualMarkers)

    // 3 — re-seed the index (quicker than diff-updating in place)
    markersIndex.clear()
    for (const m of actualMarkers) {
      markersIndex.set(markerId(m), m)
    }
  }

  /*
  This is the bridge between the map code and the Turbo based code that actually
  loads offer cards for the visible markers.

  It expects that there is a frame on the page that:
  - has the specific id you can see in the code below
  AND
  - has offers-search-frame stimulus controller initialised on it.
  */
  function loadMapOffers(url, ids, incremental) {
    let frame = document.querySelector("#offers_map_search_results")
    const list = frame.querySelector('[data-controller~="search-map-list"]')
    incremental &&= !!list
    const options = new URLSearchParams(
      JSON.parse(frame.dataset.options || "{}"),
    )

    if (incremental) {
      list.dataset.searchMapListIdsValue = JSON.stringify(ids)
    } else {
      // Map code currently doesn't recognise when user interacted with the filters without changing anything.
      // Example: Toggling all/any on Leisure filters without any filters being selected.
      // Since the map reloads itself, the sidebar also needs to do it but Stimulus will no react if URL didn't
      // actually change so we add a bust cache param to ensure the url is actually different, triggering reloading.
      frame.dataset.offersSearchFrameUrlValue =
        HB.addArrayToParams(url, ids, "place_ids") +
        `&${options}` +
        "&bustcache=" +
        Date.now()
    }
  }

  /**
   * Fetch offer hashes for marker IDs.
   *
   * @param {boolean}           incremental  – true → merge with cache; false → replace
   * @param {number[] | null=}  offers       – explicit ids (omit to use visible ones)
   */
  // The search map passes its offers url in from the config tag; the property
  // map builds it from its own config and the page params.
  function fetchOffers(incremental, offers = null, offersUrl = null) {
    const url = offersUrl || buildOffersUrl()
    const ids = offers ?? Array.from(getVisibleMarkerIds())

    loadMapOffers(url, ids, incremental)
  }

  // Stay params (checkin, checkout, groups) must be extracted client-side from
  // window.location.search because the property map opens via a Turbo Frame
  // whose request URL doesn't carry these params, and the search cookie is only
  // set when remember_search_params is present (not on direct property visits).
  function buildOffersUrl() {
    const base = buildConfig().offers_url
    if (!base) return base

    const pageParams = new URLSearchParams(window.location.search)
    const url = new URL(base, window.location.origin)

    for (const key of ["checkin", "checkout"]) {
      if (pageParams.has(key)) url.searchParams.set(key, pageParams.get(key))
    }

    const groups = pageParams.getAll("groups[]")
    if (groups.length > 0) {
      groups.forEach((v) => url.searchParams.append("groups[]", v))
    } else {
      url.searchParams.append("groups[][adults]", "2")
    }

    return url.pathname + "?" + url.searchParams.toString()
  }

  const loadGoogleMaps = (url) => {
    return window.google?.maps
      ? Promise.resolve()
      : new Promise((resolve) => {
          ;(window.HB = window.HB || {}).gmapsLoaded = resolve
          HB.loadJS(url)
        })
  }

  const buildConfig = (id = "property-map-data") => {
    const raw = document.getElementById(id)?.textContent
    if (!raw) {
      console.error(`No ${id} script tag found.`)
      return {}
    }
    const config = JSON.parse(raw || "{}")
    config.clusteringThreshold =
      config.clusteringThreshold || config.clustering_threshold || 20
    return config
  }

  const defaultMapOptions = () => {
    const isIOS = document.documentElement.classList.contains("ios")

    return {
      keyboardShortcuts: false,
      clickableIcons: false,
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      scaleControl: true,
      gestureHandling: isIOS ? "greedy" : undefined,
      cameraControl: false,
      zoomControl: Detector.isDesktop,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM,
      },
      styles: [
        { featureType: "poi.business", stylers: [{ visibility: "off" }] },
        { featureType: "poi.government", stylers: [{ visibility: "off" }] },
        { featureType: "poi.medical", stylers: [{ visibility: "off" }] },
        { featureType: "poi.school", stylers: [{ visibility: "off" }] },
      ],
    }
  }

  // --- Marker selection state ---

  let selectedMarker = null
  let previousSelectedMarker = null

  function selectedMarkerId() {
    return selectedMarker?.id ?? null
  }

  // The first-ever selection on the page: the mobile map card animates its
  // entrance only then.
  function markerSelectedOnce() {
    return previousSelectedMarker === null
  }

  function selectMarkerById(id) {
    const marker = getMarkerById(id)
    if (marker) {
      marker.select?.()
      previousSelectedMarker = selectedMarker
      selectedMarker = marker
    }
  }

  function deselectMarkerById(id) {
    const marker = getMarkerById(id)
    if (marker) {
      marker.deselect?.()
      previousSelectedMarker = selectedMarker
      selectedMarker = null
    }
  }

  window.HB = window.HB || {}
  window.HB.mapHelpers = {}
  window.HB.mapHelpers.processFetchedMarkers = processFetchedMarkers
  window.HB.mapHelpers.fetchOffers = fetchOffers
  window.HB.mapHelpers.buildFetchMarkersParams = buildFetchMarkersParams
  window.HB.mapHelpers.buildDataToMarker = buildDataToMarker
  window.HB.mapHelpers.resetMarkers = resetMarkers
  window.HB.mapHelpers.removeAllMarkers = removeAllMarkers
  window.HB.mapHelpers.getMarkerById = getMarkerById
  window.HB.mapHelpers.selectedMarkerId = selectedMarkerId
  window.HB.mapHelpers.markerSelectedOnce = markerSelectedOnce
  window.HB.mapHelpers.selectMarkerById = selectMarkerById
  window.HB.mapHelpers.deselectMarkerById = deselectMarkerById
  window.HB.mapHelpers.loadGoogleMaps = loadGoogleMaps
  window.HB.mapHelpers.buildConfig = buildConfig
  window.HB.mapHelpers.defaultMapOptions = defaultMapOptions
  window.HB.mapHelpers.boundsToBoundingBox = boundsToBoundingBox

  // for testing
  window.HB.mapHelpers._getVisibleMarkers = getVisibleMarkers
  window.HB.mapHelpers._getVisibleMarkerIds = getVisibleMarkerIds
  window.HB.mapHelpers._addNewMarkersToMap = addNewMarkersToMap
  window.HB.mapHelpers._removeStaleMarkersFromMap = removeStaleMarkersFromMap
  window.HB.mapHelpers._fetchOffers = fetchOffers
})()
